// 2. External libraries
import { useMutation } from '@tanstack/react-query';

// 4. Type imports
import type { UseMutationResult } from '@tanstack/react-query';
import type { AttributionData } from '~/types/onboarding';
import type { UpdateUserMetadataRequest } from '~/generated/api/model';

// 5. Custom hooks and utilities
import { updateMetadataV1UsersMetadataPost } from '~/generated/api/users/users';

/**
 * Payload for updating user metadata in Clerk.
 *
 * All fields optional to support partial updates.
 */
export interface UpdateUserMetadataPayload {
  onboardingComplete?: boolean | null;
  useCase?: 'personal' | 'team' | 'cicd' | 'other' | null;
  companySize?: 'solo' | '2-10' | '10-50' | '50+' | null;
  attribution?: AttributionData | null;
  termsAccepted?: boolean | null;
  termsAcceptedAt?: string | null;
}

/**
 * Response from user metadata update endpoint.
 */
export interface UpdateUserMetadataResponse {
  success: boolean;
  publicMetadata: Record<string, unknown>;
}

/**
 * Hook for updating user metadata in Clerk publicMetadata via backend API.
 *
 * Calls POST /v1/users/metadata endpoint with authenticated request.
 * Backend enforces write-once constraint for attribution data.
 *
 * Per §TS.5.1: Full generic type parameters for TanStack Query mutation.
 * Per §TS.3.1: Function declaration for named export.
 *
 * @returns UseMutationResult with mutate, mutateAsync, isLoading, isSuccess, error, reset
 *
 * @example
 * ```tsx
 * const mutation = useUserMetadataMutation();
 *
 * mutation.mutate({
 *   onboardingComplete: true,
 *   useCase: 'team',
 *   companySize: '2-10',
 * });
 * ```
 */
export function useUserMetadataMutation(): UseMutationResult<
  UpdateUserMetadataResponse,
  Error,
  UpdateUserMetadataPayload,
  unknown
> {
  return useMutation<UpdateUserMetadataResponse, Error, UpdateUserMetadataPayload, unknown>({
    mutationFn: async (payload: UpdateUserMetadataPayload) => {
      const response = await updateMetadataV1UsersMetadataPost(
        payload as UpdateUserMetadataRequest
      );
      return {
        success: response.success ?? true,
        publicMetadata: response.publicMetadata,
      };
    },
    retry: 1,
  });
}
